import React, { useState, useEffect } from 'react';
import { FiMail, FiSend, FiAlertCircle, FiCheck, FiSettings, FiUsers, FiFileText, FiX } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { launchAnnouncement, newProductDrop, standardNewsletter, testNewsletter } from '../../api/emailTemplates';
import authService from '../../utils/authService';

// Template options
const TEMPLATES = [
  { 
    id: 'standard', 
    name: 'Standard Newsletter', 
    description: 'A simple newsletter with title, content and optional call-to-action',
    subject: 'News from Grailed804' 
  },
  { 
    id: 'launch', 
    name: 'Launch Announcement', 
    description: 'Announce your official launch date to subscribers',
    subject: 'Grailed804 is Launching Soon!' 
  },
  { 
    id: 'product', 
    name: 'New Product Drop', 
    description: 'Announce a new product or collection',
    subject: 'New Drop Alert: Just Added to Grailed804' 
  },
  { 
    id: 'test', 
    name: 'Test Newsletter', 
    description: 'A simple test newsletter for trying out the system',
    subject: 'Test Newsletter from Grailed804' 
  }
];

const Newsletter: React.FC = () => {
  const location = useLocation();
  const selectedSubscriberIds = location.state?.selectedSubscriberIds || [];

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [status, setStatus] = useState<null | 'success' | 'error'>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateConfig, setTemplateConfig] = useState<{
    launchDate?: string;
    launchUrl?: string;
    productName?: string;
    productDescription?: string;
    productImageUrl?: string;
    productUrl?: string;
    title?: string;
    customContent?: string;
    ctaText?: string;
    ctaUrl?: string;
  }>({
    launchDate: new Date(new Date().setDate(new Date().getDate() + 14)).toLocaleDateString(),
    launchUrl: 'https://grailed804.com',
    productName: 'Nike Air Jordan 1 Retro High',
    productDescription: 'The iconic silhouette in a fresh new colorway.',
    productImageUrl: 'https://grailed804.com/products/sample.jpg',
    productUrl: 'https://grailed804.com/products/sample',
    title: 'Latest News from Grailed804',
    customContent: '<p>Here\'s the latest news and updates from Grailed804!</p>',
    ctaText: 'Shop Now',
    ctaUrl: 'https://grailed804.com/shop'
  });
  const [campaignStats, setCampaignStats] = useState<{ 
    recipientCount: number;
    totalSubscribers: number;
    campaignId: string;
  } | null>(null);
  
  // Initialize with test template if coming from subscribers selection
  useEffect(() => {
    if (selectedSubscriberIds.length > 0) {
      setSubject(TEMPLATES.find(t => t.id === 'test')?.subject || 'Test Newsletter from Grailed804');
      loadTemplate('test');
    }
  }, []);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim()) {
      setStatus('error');
      setStatusMessage('Please enter a subject');
      return;
    }
    
    if (!content.trim()) {
      setStatus('error');
      setStatusMessage('Please enter email content');
      return;
    }
    
    // For test mode, validate the test email
    if (isTestMode) {
      if (!testEmail.trim() || !testEmail.includes('@')) {
        setStatus('error');
        setStatusMessage('Please enter a valid email address for testing');
        return;
      }
    }
    
    try {
      setIsSending(true);
      setStatus(null);
      setCampaignStats(null);
      
      const data = await authService.apiRequest('/api/admin/newsletter/send', 'POST', {
        subject,
        content,
        ...(isTestMode && { testEmail }), // Only include testEmail in test mode
        ...(selectedSubscriberIds.length > 0 && !isTestMode && { subscriberIds: selectedSubscriberIds })
      });
      
      if (data.success) {
        setStatus('success');
        
        if (isTestMode) {
          setStatusMessage(`Test email sent successfully to ${testEmail}!`);
        } else {
          setStatusMessage(`Newsletter sent to ${data.recipientCount} of ${data.totalSubscribers} subscribers!`);
          setCampaignStats({
            recipientCount: data.recipientCount,
            totalSubscribers: data.totalSubscribers,
            campaignId: data.campaignId
          });
          
          // Clear form on successful campaign send
          setSubject('');
          setContent('');
        }
      } else {
        setStatus('error');
        setStatusMessage(data.message || 'Failed to send newsletter');
      }
    } catch (error) {
      console.error('Error sending newsletter:', error);
      setStatus('error');
      setStatusMessage('An error occurred while sending the newsletter');
    } finally {
      setIsSending(false);
    }
  };
  
  const toggleTestMode = () => {
    setIsTestMode(!isTestMode);
    setStatus(null);
    setStatusMessage('');
    setCampaignStats(null);
  };
  
  const loadTemplate = (templateId: string) => {
    // Set subject based on template
    const template = TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject);
    }
    
    // Generate content based on template
    let htmlContent = '';
    
    switch(templateId) {
      case 'standard':
        htmlContent = standardNewsletter(
          templateConfig.title || 'Newsletter',
          templateConfig.customContent || '',
          templateConfig.ctaText ? {
            text: templateConfig.ctaText,
            url: templateConfig.ctaUrl || 'https://grailed804.com'
          } : undefined
        );
        break;
        
      case 'launch':
        htmlContent = launchAnnouncement(
          templateConfig.launchDate || 'soon',
          templateConfig.launchUrl || 'https://grailed804.com'
        );
        break;
        
      case 'product':
        htmlContent = newProductDrop(
          templateConfig.productName || 'New Product',
          templateConfig.productDescription || 'Check out our latest product!',
          templateConfig.productImageUrl || 'https://grailed804.com/sample.jpg',
          templateConfig.productUrl || 'https://grailed804.com'
        );
        break;
        
      case 'test':
        htmlContent = testNewsletter();
        break;
        
      default:
        htmlContent = '';
    }
    
    setContent(htmlContent);
    setShowTemplates(false);
  };
  
  // Template configuration form renderer
  const renderTemplateConfig = (templateId: string) => {
    switch(templateId) {
      case 'standard':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Newsletter Title</label>
              <input
                type="text"
                value={templateConfig.title || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, title: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Content</label>
              <textarea
                rows={4}
                value={templateConfig.customContent || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, customContent: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="HTML content for your newsletter"
              />
              <p className="text-xs text-gray-400 mt-1">You can use HTML tags for formatting</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Call-to-action Text</label>
                <input
                  type="text"
                  value={templateConfig.ctaText || ''}
                  onChange={(e) => setTemplateConfig({...templateConfig, ctaText: e.target.value})}
                  className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                  placeholder="Shop Now"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">CTA URL</label>
                <input
                  type="url"
                  value={templateConfig.ctaUrl || ''}
                  onChange={(e) => setTemplateConfig({...templateConfig, ctaUrl: e.target.value})}
                  className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                  placeholder="https://grailed804.com/shop"
                />
              </div>
            </div>
          </div>
        );
        
      case 'launch':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Launch Date</label>
              <input
                type="text"
                value={templateConfig.launchDate || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, launchDate: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="June 15, 2023"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Launch URL</label>
              <input
                type="url"
                value={templateConfig.launchUrl || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, launchUrl: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="https://grailed804.com"
              />
            </div>
          </div>
        );
        
      case 'product':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Product Name</label>
              <input
                type="text"
                value={templateConfig.productName || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, productName: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="Nike Air Jordan 1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Product Description</label>
              <textarea
                rows={2}
                value={templateConfig.productDescription || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, productDescription: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="The iconic silhouette in a fresh new colorway."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Product Image URL</label>
              <input
                type="url"
                value={templateConfig.productImageUrl || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, productImageUrl: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="https://grailed804.com/images/product.jpg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Product URL</label>
              <input
                type="url"
                value={templateConfig.productUrl || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, productUrl: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="https://grailed804.com/products/nike-air-jordan-1"
              />
            </div>
          </div>
        );
        
      case 'test':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Test Title</label>
              <input
                type="text"
                value={templateConfig.title || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, title: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Content</label>
              <textarea
                rows={4}
                value={templateConfig.customContent || ''}
                onChange={(e) => setTemplateConfig({...templateConfig, customContent: e.target.value})}
                className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="HTML content for your test newsletter"
              />
              <p className="text-xs text-gray-400 mt-1">You can use HTML tags for formatting</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Call-to-action Text</label>
                <input
                  type="text"
                  value={templateConfig.ctaText || ''}
                  onChange={(e) => setTemplateConfig({...templateConfig, ctaText: e.target.value})}
                  className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                  placeholder="Shop Now"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">CTA URL</label>
                <input
                  type="url"
                  value={templateConfig.ctaUrl || ''}
                  onChange={(e) => setTemplateConfig({...templateConfig, ctaUrl: e.target.value})}
                  className="w-full bg-background-light border border-gray-700 rounded px-3 py-2 text-white"
                  placeholder="https://grailed804.com/shop"
                />
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="bg-background-dark min-h-screen text-white p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold flex items-center mb-8">
          <FiMail className="mr-3 text-purple-500" /> 
          Send Newsletter
        </h1>
        
        {/* Mode toggle */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${!isTestMode ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  onClick={() => !isTestMode || toggleTestMode()}>
              <FiUsers className="inline mr-1" /> All Subscribers
            </span>
            <span className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${isTestMode ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  onClick={() => isTestMode || toggleTestMode()}>
              <FiSettings className="inline mr-1" /> Test Mode
            </span>
          </div>
          
          <div className="text-sm text-gray-400">
            {isTestMode ? 'Send to a single test email' : 'Send to all subscribers'}
          </div>
        </div>
        
        {/* Status message */}
        {status && (
          <div 
            className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
              status === 'success' ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
            }`}
          >
            {status === 'success' ? (
              <FiCheck className="text-green-500 text-lg mt-0.5" />
            ) : (
              <FiAlertCircle className="text-red-500 text-lg mt-0.5" />
            )}
            <div>
              <h3 className={`font-medium ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {status === 'success' ? 'Success' : 'Error'}
              </h3>
              <p className="text-white/80">{statusMessage}</p>
              
              {campaignStats && (
                <div className="mt-2 text-sm text-white/70">
                  <p>Campaign ID: {campaignStats.campaignId}</p>
                  <p>Sent to {campaignStats.recipientCount} of {campaignStats.totalSubscribers} subscribers</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Template selector modal */}
        {showTemplates && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-700">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <FiFileText className="text-purple-500" /> Choose a Template
                </h3>
                <button 
                  onClick={() => setShowTemplates(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <div className="p-4">
                <div className="grid gap-4 mb-6">
                  {TEMPLATES.map(template => (
                    <div 
                      key={template.id}
                      className="border border-gray-700 rounded-lg p-4 cursor-pointer hover:border-purple-500 transition-colors"
                      onClick={() => {
                        const activeTab = document.getElementById(`tab-${template.id}`);
                        if (activeTab) {
                          document.querySelectorAll('[role="tab"]').forEach(tab => 
                            tab.setAttribute('aria-selected', 'false')
                          );
                          activeTab.setAttribute('aria-selected', 'true');
                          
                          document.querySelectorAll('[role="tabpanel"]').forEach(panel => 
                            panel.classList.add('hidden')
                          );
                          document.getElementById(`panel-${template.id}`)?.classList.remove('hidden');
                        }
                      }}
                    >
                      <h4 className="font-medium text-white">{template.name}</h4>
                      <p className="text-sm text-gray-400">{template.description}</p>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-gray-700 pt-4">
                  <div role="tablist" className="flex border-b border-gray-700">
                    {TEMPLATES.map((template, index) => (
                      <button
                        key={template.id}
                        id={`tab-${template.id}`}
                        role="tab"
                        aria-selected={index === 0 ? 'true' : 'false'}
                        className="px-4 py-2 text-sm font-medium focus:outline-none"
                        onClick={() => {
                          document.querySelectorAll('[role="tab"]').forEach(tab => 
                            tab.setAttribute('aria-selected', 'false')
                          );
                          document.getElementById(`tab-${template.id}`)?.setAttribute('aria-selected', 'true');
                          
                          document.querySelectorAll('[role="tabpanel"]').forEach(panel => 
                            panel.classList.add('hidden')
                          );
                          document.getElementById(`panel-${template.id}`)?.classList.remove('hidden');
                        }}
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>
                  
                  <div className="mt-4">
                    {TEMPLATES.map((template, index) => (
                      <div
                        key={template.id}
                        id={`panel-${template.id}`}
                        role="tabpanel"
                        className={index === 0 ? '' : 'hidden'}
                      >
                        {renderTemplateConfig(template.id)}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowTemplates(false)}
                    className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const selectedTab = document.querySelector('[role="tab"][aria-selected="true"]');
                      const templateId = selectedTab?.id.replace('tab-', '') || 'standard';
                      loadTemplate(templateId);
                    }}
                    className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 rounded text-white transition-colors"
                  >
                    Use Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email template selector button */}
          <button
            type="button"
            onClick={() => setShowTemplates(true)}
            className="flex items-center gap-2 mb-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 transition-colors rounded text-white"
          >
            <FiFileText />
            Choose Email Template
          </button>
          
          {/* Test email input (only shown in test mode) */}
          {isTestMode && (
            <div>
              <label htmlFor="testEmail" className="block text-sm font-medium text-gray-300 mb-2">
                Test Email Address
              </label>
              <input
                id="testEmail"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address for testing"
                className="w-full bg-background-light border border-gray-700 rounded-lg py-3 px-4 text-white focus:border-purple-500 focus:outline-none"
                disabled={isSending}
              />
            </div>
          )}
          
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
              Email Subject
            </label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter newsletter subject"
              className="w-full bg-background-light border border-gray-700 rounded-lg py-3 px-4 text-white focus:border-purple-500 focus:outline-none"
              disabled={isSending}
            />
          </div>
          
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-2">
              Email Content (HTML)
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your newsletter HTML content here"
              rows={15}
              className="w-full bg-background-light border border-gray-700 rounded-lg py-3 px-4 text-white focus:border-purple-500 focus:outline-none font-mono text-sm"
              disabled={isSending}
            />
            <p className="mt-2 text-sm text-gray-400">
              You can use HTML to format your newsletter. Include inline CSS for the best compatibility.
            </p>
          </div>
          
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSending}
              className="flex items-center justify-center gap-2 w-full py-3 px-6 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
            >
              {isSending ? (
                <>
                  <span className="inline-block w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                  {isTestMode ? 'Sending Test...' : 'Sending...'}
                </>
              ) : (
                <>
                  <FiSend />
                  {isTestMode ? 'Send Test Email' : 'Send Newsletter'}
                </>
              )}
            </button>
          </div>
          
          <div className="bg-background-light/50 border border-gray-800 rounded-lg p-4 text-sm text-gray-300">
            <h3 className="font-medium mb-2 flex items-center">
              <FiAlertCircle className="mr-2 text-purple-500" />
              Before sending:
            </h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Double-check your content for any errors or typos</li>
              <li>Test your HTML to ensure it looks good on different email clients</li>
              <li>Make sure your subject line is compelling and clear</li>
              <li>
                {isTestMode 
                  ? 'Send a test email to verify the appearance before sending to all subscribers'
                  : 'Consider sending a test email first to verify everything looks correct'}
              </li>
            </ul>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Newsletter; 